import React, { useState, useEffect, useRef } from 'react';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { 
  Calculator, TrendingUp, DollarSign, CheckCircle, ArrowRight,
  PiggyBank, Clock, Users, BarChart, Shield, Zap, Star, Rocket,
  Crown, Cloud, Play, Sparkles, ChevronRight, Check
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from 'sonner';
import { useBooking } from '../context/BookingContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Animated Number Component
const AnimatedValue = ({ value, prefix = '', suffix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numericValue)) {
      setDisplayValue(value);
      return;
    }
    let start = 0;
    const duration = 1500;
    const increment = numericValue / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= numericValue) {
        setDisplayValue(numericValue);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, value]);

  return (
    <span ref={ref}>
      {prefix}{typeof displayValue === 'number' ? displayValue.toLocaleString() : displayValue}{suffix}
    </span>
  );
};

const ROICalculatorPage = () => {
  const { openBooking, openContact } = useBooking();
  
  // CMS settings state
  const [cmsSettings, setCmsSettings] = useState(null);
  
  // Calculator state with defaults (will be overridden by CMS)
  const [staffCount, setStaffCount] = useState(100);
  const [currentSoftwareCost, setCurrentSoftwareCost] = useState(45);
  const [adminHours, setAdminHours] = useState(40);
  const [selectedTier, setSelectedTier] = useState('growth');

  // Fetch CMS settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/cms/roi-settings`);
        if (response.ok) {
          const data = await response.json();
          setCmsSettings(data);
          // Set defaults from CMS
          if (data.default_staff) setStaffCount(data.default_staff);
          if (data.default_admin_hours) setAdminHours(data.default_admin_hours);
        }
      } catch (error) {
        console.error('Failed to fetch ROI settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Get CMS values with fallbacks
  const heroTitle = cmsSettings?.hero_title || 'Calculate Your ROI';
  const heroSubtitle = cmsSettings?.hero_subtitle || 'See how much time and money GetCare 360 can save your NDIS organisation';
  const timeSavingsPercent = cmsSettings?.time_savings_percent || 60;
  const hourlyRate = cmsSettings?.default_hourly_rate || 45;
  const ctaTitle = cmsSettings?.cta_title || 'Ready to See These Savings?';
  const ctaSubtitle = cmsSettings?.cta_subtitle || 'Book a personalised demo and we\'ll calculate your exact ROI';
  const ctaButtonText = cmsSettings?.cta_button_text || 'Book Your Free Demo';
  
  // Investment Packages
  const investmentPackages = [
    {
      id: 'starter',
      name: 'STARTER',
      icon: Rocket,
      staffRange: '5-15 Staff',
      buildPrice: 18500,
      hostingPrice: 390,
      color: 'care-teal',
      gradient: 'from-care-teal to-emerald-500',
      features: ['Full Platform Ownership', 'Branded Web + Mobile', 'Core NDIS Modules', 'Standard Support']
    },
    {
      id: 'growth',
      name: 'GROWTH',
      icon: TrendingUp,
      staffRange: '15-50 Staff',
      buildPrice: 32000,
      hostingPrice: 690,
      color: 'tech-blue',
      gradient: 'from-tech-blue to-blue-500',
      features: ['Everything in Starter', 'Advanced Rostering', 'AI Compliance Tools', 'Priority Support'],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'ENTERPRISE',
      icon: Crown,
      staffRange: '50+ Staff',
      buildPrice: 55000,
      hostingPrice: 990,
      color: 'purple-600',
      gradient: 'from-purple-600 to-purple-500',
      features: ['Everything in Growth', 'Full AI Suite', 'Multi-Site Support', 'Dedicated Manager']
    }
  ];

  // Get selected package
  const currentPackage = investmentPackages.find(p => p.id === selectedTier) || investmentPackages[1];
  
  // Calculations
  const monthlySaasCost = staffCount * currentSoftwareCost;
  const annualSaasCost = monthlySaasCost * 12;
  
  // GetCare costs from selected package
  const buildCost = currentPackage.buildPrice;
  const monthlyHosting = currentPackage.hostingPrice;
  const annualGetCareCost = buildCost + (monthlyHosting * 12);
  
  // Time savings - use CMS values
  const monthlyTimeSavings = (adminHours * timeSavingsPercent / 100) * hourlyRate * 4;
  const annualTimeSavings = monthlyTimeSavings * 12;
  
  // Total savings
  const totalFirstYearSavings = annualSaasCost - annualGetCareCost + annualTimeSavings;
  const twoYearSavings = (annualSaasCost * 2) - (buildCost + (monthlyHosting * 24)) + (annualTimeSavings * 2);
  const threeYearSavings = (annualSaasCost * 3) - (buildCost + (monthlyHosting * 36)) + (annualTimeSavings * 3);
  const fiveYearSavings = (annualSaasCost * 5) - (buildCost + (monthlyHosting * 60)) + (annualTimeSavings * 5);
  const roiPercentage = ((totalFirstYearSavings / annualGetCareCost) * 100).toFixed(0);

  // Auto-select tier based on staff count
  useEffect(() => {
    if (staffCount <= 200) setSelectedTier('starter');
    else if (staffCount <= 500) setSelectedTier('growth');
    else setSelectedTier('enterprise');
  }, [staffCount]);
  
  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="ROI Calculator | GetCare 360 - Calculate Your NDIS Software Savings"
        description="Use our free ROI calculator to see how much your NDIS organisation can save with GetCare 360's one-time investment model vs traditional SaaS subscriptions."
        keywords="NDIS software ROI calculator, disability care software savings, NDIS provider cost calculator, software investment calculator"
        canonicalUrl="/roi-calculator"
        page="roi-calculator"
      />
      <Toaster position="top-right" richColors />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary-navy via-[#0d2847] to-[#0a1f3d] text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-care-teal/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }}></div>
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-tech-blue/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }}></div>
          
          {/* Grid */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-care-teal/20 rounded-full border border-care-teal/30 mb-6">
              <Calculator className="text-care-teal mr-2" size={20} />
              <span className="text-sm font-medium">Investment & ROI Calculator</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="roi-hero-title">
              {heroTitle.includes('ROI') ? (
                <>Calculate Your <span className="bg-gradient-to-r from-care-teal to-tech-blue bg-clip-text text-transparent">Savings</span></>
              ) : (
                heroTitle
              )}
            </h1>
            <p className="text-xl text-white/80 mb-4">
              {heroSubtitle}
            </p>
            <p className="text-base text-white/60 max-w-3xl mx-auto">
              Own your platform. No per-user fees. Typical providers save 40-60% over 3 years.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 -mt-10 relative z-20" data-testid="roi-calculator-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left - Input Section */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-2 border-tech-blue/30 shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-tech-blue to-tech-blue/80 p-5 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <Users className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold">Your Organisation</h3>
                      <p className="text-white/70 text-sm">Adjust to match your setup</p>
                    </div>
                  </div>
                </div>
                
                <CardContent className="p-6 space-y-6">
                  {/* Staff Count */}
                  <div className="bg-surface rounded-xl p-5 border border-border-line">
                    <label className="block text-sm font-medium text-primary-navy mb-3">
                      Number of Staff
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="5000"
                      step="1"
                      value={staffCount}
                      onChange={(e) => setStaffCount(Number(e.target.value))}
                      className="w-full h-2 bg-tech-blue/20 rounded-lg appearance-none cursor-pointer accent-tech-blue"
                      data-testid="staff-slider"
                    />
                    <div className="flex justify-between items-center mt-3">
                      <span className="text-xs text-text-secondary">1</span>
                      <div className="text-center">
                        <span className="text-4xl font-bold text-tech-blue">{staffCount.toLocaleString()}</span>
                        <span className="text-text-secondary ml-1">staff</span>
                      </div>
                      <span className="text-xs text-text-secondary">5,000</span>
                    </div>
                  </div>
                  
                  {/* Current Software Cost */}
                  <div className="bg-surface rounded-xl p-5 border border-border-line">
                    <label className="block text-sm font-medium text-primary-navy mb-3">
                      Current SaaS Cost Per User/Month
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary font-bold">$</span>
                      <input
                        type="number"
                        value={currentSoftwareCost}
                        onChange={(e) => setCurrentSoftwareCost(Number(e.target.value))}
                        className="w-full pl-10 pr-4 py-3 border-2 border-border-line rounded-xl focus:outline-none focus:border-tech-blue text-xl font-semibold"
                        data-testid="cost-input"
                      />
                    </div>
                    <p className="text-xs text-text-secondary mt-2">
                      Industry range: $15-$200 per user/month
                    </p>
                  </div>
                  
                  {/* Admin Hours */}
                  <div className="bg-surface rounded-xl p-5 border border-border-line">
                    <label className="block text-sm font-medium text-primary-navy mb-3">
                      Weekly Admin Hours
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="80"
                      value={adminHours}
                      onChange={(e) => setAdminHours(Number(e.target.value))}
                      className="w-full h-2 bg-care-teal/20 rounded-lg appearance-none cursor-pointer accent-care-teal"
                      data-testid="hours-slider"
                    />
                    <div className="flex justify-between items-center mt-3">
                      <span className="text-xs text-text-secondary">10</span>
                      <div className="text-center">
                        <span className="text-4xl font-bold text-care-teal">{adminHours}</span>
                        <span className="text-text-secondary ml-1">hrs/week</span>
                      </div>
                      <span className="text-xs text-text-secondary">80</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right - Results & Investment Model */}
            <div className="lg:col-span-3 space-y-6">
              {/* ROI Summary Card - Shows savings without GetCare pricing */}
              <div className="bg-gradient-to-br from-primary-navy via-[#0d2847] to-tech-blue rounded-2xl p-6 text-white shadow-xl" data-testid="savings-summary-card">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-white/80">Your Projected Savings</h3>
                  <p className="text-sm text-white/50 mt-1">Based on your inputs vs traditional SaaS model</p>
                </div>
                
                {/* Main Savings Display */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-5 bg-white/10 backdrop-blur rounded-xl border border-white/10">
                    <p className="text-xs text-white/60 uppercase tracking-wide mb-2">Year 1 Savings</p>
                    <div className="text-2xl md:text-3xl font-bold text-care-teal" data-testid="year1-savings">
                      $<AnimatedValue value={Math.max(0, totalFirstYearSavings)} />
                    </div>
                    <p className="text-xs text-white/50 mt-2">Including time savings</p>
                  </div>
                  <div className="text-center p-5 bg-white/10 backdrop-blur rounded-xl border border-white/10">
                    <p className="text-xs text-white/60 uppercase tracking-wide mb-2">Year 2 Savings</p>
                    <div className="text-2xl md:text-3xl font-bold text-white" data-testid="year2-savings">
                      $<AnimatedValue value={Math.max(0, twoYearSavings)} />
                    </div>
                    <p className="text-xs text-white/50 mt-2">Cumulative total</p>
                  </div>
                  <div className="text-center p-5 bg-white/10 backdrop-blur rounded-xl border border-care-teal/30">
                    <p className="text-xs text-white/60 uppercase tracking-wide mb-2">3-Year Savings</p>
                    <div className="text-2xl md:text-3xl font-bold text-care-teal" data-testid="year3-savings">
                      $<AnimatedValue value={Math.max(0, threeYearSavings)} />
                    </div>
                    <p className="text-xs text-care-teal mt-2">Most popular</p>
                  </div>
                  <div className="text-center p-5 bg-white/10 backdrop-blur rounded-xl border border-white/10">
                    <p className="text-xs text-white/60 uppercase tracking-wide mb-2">5-Year Savings</p>
                    <div className="text-2xl md:text-3xl font-bold text-white" data-testid="year5-savings">
                      $<AnimatedValue value={Math.max(0, fiveYearSavings)} />
                    </div>
                    <p className="text-xs text-white/50 mt-2">Long-term value</p>
                  </div>
                </div>

                {/* Secondary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-white/5 rounded-lg">
                    <p className="text-[10px] text-white/50 uppercase tracking-wide">ROI</p>
                    <div className="text-xl font-bold text-care-teal" data-testid="roi-percentage">
                      <AnimatedValue value={Math.max(0, parseInt(roiPercentage))} suffix="%" />
                    </div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg">
                    <p className="text-[10px] text-white/50 uppercase tracking-wide">Admin Time Saved</p>
                    <div className="text-xl font-bold text-white">60%</div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg">
                    <p className="text-[10px] text-white/50 uppercase tracking-wide">Hours/Week</p>
                    <div className="text-xl font-bold text-white">
                      <AnimatedValue value={Math.round(adminHours * 0.6)} />
                    </div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg">
                    <p className="text-[10px] text-white/50 uppercase tracking-wide">Annual Time Value</p>
                    <div className="text-xl font-bold text-care-teal">
                      $<AnimatedValue value={annualTimeSavings} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Investment Model Cards */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-primary-navy">Select Investment Package</h3>
                  <span className="text-sm text-care-teal font-medium">
                    Recommended based on {staffCount.toLocaleString()} staff
                  </span>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  {investmentPackages.map((pkg) => {
                    const Icon = pkg.icon;
                    const isSelected = selectedTier === pkg.id;
                    return (
                      <div
                        key={pkg.id}
                        onClick={() => setSelectedTier(pkg.id)}
                        className={`relative cursor-pointer transition-all duration-300 ${
                          isSelected ? 'scale-[1.02]' : 'hover:scale-[1.01]'
                        }`}
                      >
                        {pkg.popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                            <span className="bg-tech-blue text-white text-[10px] font-bold px-3 py-1 rounded-full">
                              POPULAR
                            </span>
                          </div>
                        )}
                        
                        <Card className={`h-full overflow-hidden transition-all ${
                          isSelected 
                            ? `border-2 border-${pkg.color} shadow-lg shadow-${pkg.color}/20` 
                            : 'border border-border-line hover:border-gray-300'
                        }`}>
                          <div className={`p-4 ${isSelected ? `bg-gradient-to-r ${pkg.gradient} text-white` : 'bg-surface'}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                isSelected ? 'bg-white/20' : `bg-${pkg.color}/10`
                              }`}>
                                <Icon className={isSelected ? 'text-white' : `text-${pkg.color}`} size={20} />
                              </div>
                              <div>
                                <h4 className={`font-bold ${isSelected ? 'text-white' : 'text-primary-navy'}`}>{pkg.name}</h4>
                                <p className={`text-xs ${isSelected ? 'text-white/70' : 'text-text-secondary'}`}>{pkg.staffRange}</p>
                              </div>
                            </div>
                          </div>
                          
                          <CardContent className="p-4">
                            {/* Features List instead of pricing */}
                            <ul className="space-y-2">
                              {pkg.features.map((feature, idx) => (
                                <li key={idx} className="flex items-center gap-2 text-sm text-text-secondary">
                                  <Check className={`text-${pkg.color} flex-shrink-0`} size={14} />
                                  {feature}
                                </li>
                              ))}
                            </ul>
                            
                            {isSelected && (
                              <div className="mt-4 p-3 bg-care-teal/5 rounded-lg border border-care-teal/20 text-center">
                                <p className="text-xs text-text-secondary">Best for your organisation</p>
                                <p className="text-sm font-semibold text-care-teal mt-1">Book a demo to see pricing</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Why GetCare Saves You More - Replaces Cost Comparison */}
              <Card className="border-2 border-border-line">
                <CardContent className="p-6">
                  <h4 className="font-bold text-primary-navy mb-4 flex items-center gap-2">
                    <TrendingUp className="text-tech-blue" size={20} />
                    Why GetCare Saves You More
                  </h4>
                  
                  <div className="space-y-4">
                    {/* SaaS Problem */}
                    <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <DollarSign className="text-red-600" size={18} />
                        </div>
                        <div>
                          <h5 className="font-semibold text-red-700">Traditional SaaS Problem</h5>
                          <p className="text-sm text-red-600 mt-1">
                            At {staffCount.toLocaleString()} staff × ${currentSoftwareCost}/user/month = <strong>${monthlySaasCost.toLocaleString()}/month</strong> forever
                          </p>
                          <p className="text-xs text-red-500 mt-2">Costs increase every time you hire new staff</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* GetCare Solution */}
                    <div className="bg-care-teal/5 rounded-xl p-4 border border-care-teal/20">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-care-teal/20 flex items-center justify-center flex-shrink-0">
                          <Check className="text-care-teal" size={18} />
                        </div>
                        <div>
                          <h5 className="font-semibold text-care-teal">GetCare Solution</h5>
                          <ul className="text-sm text-text-secondary mt-2 space-y-2">
                            <li className="flex items-center gap-2">
                              <Check className="text-care-teal" size={14} />
                              One-time build investment - <strong>own your platform</strong>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="text-care-teal" size={14} />
                              Fixed monthly hosting - <strong>no per-user fees</strong>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="text-care-teal" size={14} />
                              Add unlimited staff at <strong>no extra cost</strong>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    {/* Time Savings */}
                    <div className="bg-tech-blue/5 rounded-xl p-4 border border-tech-blue/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Clock className="text-tech-blue" size={20} />
                          <div>
                            <span className="font-medium text-primary-navy">Time Savings</span>
                            <p className="text-xs text-text-secondary">Based on {adminHours} hours/week admin work</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-tech-blue">
                            {Math.round(adminHours * 0.6)} hrs
                          </span>
                          <p className="text-xs text-text-secondary">saved per week</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Button onClick={openBooking} className="flex-1 bg-care-teal hover:bg-care-teal/90 text-white py-6 text-lg group"
                        data-testid="get-proposal-btn"
                      >
                      <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
                      Book a Demo
                    </Button>
                    <Link to="/features-pricing" className="flex-1">
                      <Button variant="outline" className="w-full border-2 border-primary-navy text-primary-navy hover:bg-primary-navy hover:text-white py-6 text-lg">
                        View Full Features
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
              Why GetCare Saves You <span className="text-tech-blue">More</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: DollarSign,
                title: 'No Per-User Fees',
                description: 'Unlike SaaS platforms that charge per user, GetCare has a one-time build fee. Add staff without increasing costs.'
              },
              {
                icon: Shield,
                title: 'Own Your Software',
                description: 'Full ownership option means no vendor lock-in. Your platform, your data, your rules.'
              },
              {
                icon: Zap,
                title: 'AI-Powered Efficiency',
                description: 'Reduce admin hours by 60% with automated compliance monitoring, reporting, and smart alerts.'
              },
              {
                icon: BarChart,
                title: 'Predictable Costs',
                description: 'Fixed monthly hosting means you know exactly what you\'ll pay. No surprise per-user charges as you grow.'
              },
              {
                icon: Clock,
                title: 'Time Savings',
                description: 'Custom-built for your workflow means less time adapting to software and more time on care delivery.'
              },
              {
                icon: TrendingUp,
                title: 'Long-Term Value',
                description: 'The longer you use GetCare, the more you save. Year 2+ costs are just hosting fees.'
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border border-border-line hover:border-tech-blue hover:-translate-y-1 transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-xl bg-tech-blue/10 flex items-center justify-center mb-4 group-hover:bg-tech-blue group-hover:scale-110 transition-all">
                      <Icon className="text-tech-blue group-hover:text-white transition-colors" size={26} />
                    </div>
                    <h3 className="text-lg font-bold text-primary-navy mb-2 group-hover:text-tech-blue transition-colors">{benefit.title}</h3>
                    <p className="text-text-secondary">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-navy via-tech-blue to-purple-900 text-white relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-care-teal/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }}></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
            <Sparkles className="text-care-teal mr-2" size={16} />
            <span className="text-sm font-medium">Australian Made & Supported</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            {ctaTitle.includes('Savings') ? (
              <>Ready to Start <span className="text-care-teal">Saving?</span></>
            ) : (
              ctaTitle
            )}
          </h2>
          <p className="text-xl text-white/80 mb-8">
            {ctaSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openBooking} className="bg-care-teal hover:bg-care-teal/90 text-white px-10 py-6 text-lg group shadow-lg shadow-care-teal/30" data-testid="final-cta">
              <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
              {ctaButtonText}
            </Button>
            <Link to="/features-pricing">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg">
                View Full Pricing
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ROICalculatorPage;
